import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  Link,
  Avatar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const CertificationCard = styled(Card)(({ theme, achieved, inProgress }) => ({
  border: `2px solid ${
    achieved ? theme.palette.success.main :
    inProgress ? theme.palette.warning.main :
    theme.palette.grey[300]
  }`,
  backgroundColor: achieved ? theme.palette.success.light :
                  inProgress ? theme.palette.warning.light :
                  theme.palette.grey[50],
  marginBottom: theme.spacing(2),
}));

const LevelCard = styled(Paper)(({ theme, active }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  border: `2px solid ${active ? theme.palette.primary.main : theme.palette.grey[200]}`,
  backgroundColor: active ? theme.palette.primary.light : theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const TourismCertifications = ({ calculation, calculator }) => {
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    greenKey: false,
    leed: false,
    iso14001: false,
    other: false,
    roadmap: false
  });

  // Sertifikasyon tanımları
  const certifications = {
    greenKey: {
      name: 'Green Key',
      description: 'Dünya çapında en büyük çevre sertifikasyon programı',
      website: 'https://www.greenkey.global/',
      logo: '🗝️',
      levels: [
        {
          name: 'Bronze',
          threshold: 20,
          description: 'Temel çevre yönetimi standartları',
          requirements: [
            'Enerji ve su tüketim takibi',
            'Atık ayrıştırma programı',
            'Personel çevre eğitimi',
            'Çevre politikası oluşturma'
          ],
          benefits: ['Çevre bilincinin artması', 'Maliyetlerde azalma', 'Marka imajı güçlenmesi']
        },
        {
          name: 'Silver',
          threshold: 15,
          description: 'Gelişmiş sürdürülebilirlik uygulamaları',
          requirements: [
            'Enerji verimli ekipman kullanımı',
            'Su tasarrufu sistemleri',
            'Yerel ürün tedariki',
            'Misafir bilinçlendirme programları'
          ],
          benefits: ['İşletme maliyetlerinde %15-25 azalma', 'Çevre dostu müşteri çekme', 'Rekabet avantajı']
        },
        {
          name: 'Gold',
          threshold: 12,
          description: 'Üstün çevre performansı',
          requirements: [
            'Yenilenebilir enerji kullanımı',
            'Geri dönüştürülmüş su sistemleri',
            'Organik/yerel gıda %50+',
            'Karbon ayak izi izleme'
          ],
          benefits: ['Premium fiyatlandırma imkanı', 'Uluslararası tanınırlık', 'Yatırımcı ilgisi']
        },
        {
          name: 'Platinum',
          threshold: 8,
          description: 'Dünya standartında sürdürülebilirlik',
          requirements: [
            'Net sıfır karbon hedefi',
            'Tamamen yenilenebilir enerji',
            'Sıfır atık programı',
            'Biyoçeşitlilik koruma projeleri'
          ],
          benefits: ['Sektör lideri konumu', 'Medya ilgisi', 'Sürdürülebilirlik ödülleri']
        }
      ]
    },
    leed: {
      name: 'LEED (Leadership in Energy and Environmental Design)',
      description: 'Yeşil bina sertifikasyon sistemi',
      website: 'https://www.usgbc.org/leed',
      logo: '🏢',
      levels: [
        {
          name: 'Certified',
          threshold: 18,
          description: 'Temel yeşil bina standardı',
          requirements: [
            'Enerji verimliliği %10 artış',
            'Su kullanımında %20 azalma',
            'Sürdürülebilir malzeme kullanımı',
            'İç hava kalitesi standartları'
          ],
          benefits: ['Enerji maliyetlerinde tasarruf', 'Sağlıklı iç ortam', 'Emlak değeri artışı']
        },
        {
          name: 'Silver',
          threshold: 14,
          description: 'İyi çevre performansı',
          requirements: [
            'Enerji verimliliği %20 artış',
            'Su kullanımında %30 azalma',
            'Geri dönüştürülmüş malzeme %20+',
            'Toplu taşıma erişimi'
          ],
          benefits: ['%20-30 işletme maliyeti azalması', 'Çalışan verimliliği artışı', 'Marka değeri']
        },
        {
          name: 'Gold',
          threshold: 10,
          description: 'Yüksek sürdürülebilirlik',
          requirements: [
            'Enerji verimliliği %30 artış',
            'Su kullanımında %40 azalma',
            'Yenilenebilir enerji %25+',
            'Yeşil çatı/duvar uygulamaları'
          ],
          benefits: ['Premium segment konumlandırma', 'Düşük işletme maliyetleri', 'Kurumsal prestij']
        },
        {
          name: 'Platinum',
          threshold: 7,
          description: 'En yüksek çevre standardı',
          requirements: [
            'Net pozitif enerji binası',
            'Su kullanımında %50+ azalma',
            'Yenilenebilir enerji %100',
            'BREEAM/WELL sertifikasyonları'
          ],
          benefits: ['Dünya çapında tanınırlık', 'Sürdürülebilirlik lideri', 'Yatırım çekiciliği']
        }
      ]
    },
    iso14001: {
      name: 'ISO 14001 Çevre Yönetim Sistemi',
      description: 'Uluslararası çevre yönetim standardı',
      website: 'https://www.iso.org/iso-14001-environmental-management.html',
      logo: '📋',
      levels: [
        {
          name: 'Basic Implementation',
          threshold: 16,
          description: 'Temel çevre yönetim sistemi',
          requirements: [
            'Çevre politikası oluşturma',
            'Çevre hedefleri belirleme',
            'Çevre etki değerlendirmesi',
            'İç denetim sistemi'
          ],
          benefits: ['Yasal uyum sağlama', 'Risk yönetimi', 'Süreç iyileştirme']
        },
        {
          name: 'Advanced Implementation',
          threshold: 12,
          description: 'Gelişmiş çevre yönetimi',
          requirements: [
            'Yaşam döngüsü değerlendirmesi',
            'Tedarikçi çevre değerlendirmesi',
            'Çevre performans göstergeleri',
            'Sürekli iyileştirme programı'
          ],
          benefits: ['Operasyonel verimlilik', 'Maliyet optimizasyonu', 'Stakeholder güveni']
        }
      ]
    }
  };

  // Diğer sertifikasyonlar
  const otherCertifications = [
    {
      name: 'BREEAM',
      description: 'Building Research Establishment Environmental Assessment Method',
      focus: 'Bina çevre performansı',
      website: 'https://www.breeam.com/',
      logo: '🏗️'
    },
    {
      name: 'EDGE',
      description: 'Excellence in Design for Greater Efficiencies',
      focus: 'Gelişmekte olan ülkeler için yeşil bina',
      website: 'https://edgebuildings.com/',
      logo: '⚡'
    },
    {
      name: 'EarthCheck',
      description: 'Turizm endüstrisi için sürdürülebilirlik sertifikasyonu',
      focus: 'Turizm işletmeleri',
      website: 'https://earthcheck.org/',
      logo: '🌍'
    },
    {
      name: 'Travelife',
      description: 'Otel ve tur operatörleri için sürdürülebilirlik',
      focus: 'Turizm sürdürülebilirliği',
      website: 'https://www.travelife.info/',
      logo: '✈️'
    }
  ];

  // Mevcut performansı değerlendir
  const currentStatus = useMemo(() => {
    if (!calculation || !calculator) return null;

    const perRoomNight = calculation.perRoomNight;
    const status = calculator.getCertificationStatus(perRoomNight);
    
    const evaluations = {};
    Object.entries(certifications).forEach(([key, cert]) => {
      const achieved = [];
      const inProgress = [];
      const target = [];

      cert.levels.forEach(level => {
        if (perRoomNight <= level.threshold) {
          achieved.push(level);
        } else if (perRoomNight <= level.threshold * 1.2) {
          inProgress.push(level);
        } else {
          target.push(level);
        }
      });

      evaluations[key] = { achieved, inProgress, target };
    });

    return { ...status, evaluations };
  }, [calculation, calculator]);

  const handleSectionToggle = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getProgressPercentage = (current, target) => {
    return Math.min((target / current) * 100, 100);
  };

  const renderCertificationLevel = (cert, level, status, perRoomNight) => {
    const achieved = perRoomNight <= level.threshold;
    const inProgress = !achieved && perRoomNight <= level.threshold * 1.2;
    const progress = achieved ? 100 : getProgressPercentage(perRoomNight, level.threshold);

    return (
      <LevelCard key={level.name} active={achieved || inProgress}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6">
            {cert.logo} {level.name}
          </Typography>
          <Chip
            label={achieved ? '✅ Ulaşıldı' : inProgress ? '⚠️ Yakın' : '🎯 Hedef'}
            color={achieved ? 'success' : inProgress ? 'warning' : 'default'}
          />
        </Box>
        
        <Typography variant="body2" paragraph>
          {level.description}
        </Typography>
        
        <Typography variant="caption" color="textSecondary">
          Hedef: ≤ {level.threshold} kg CO₂e/oda-gece
        </Typography>
        
        <LinearProgress
          variant="determinate"
          value={progress}
          color={achieved ? 'success' : inProgress ? 'warning' : 'primary'}
          sx={{ mt: 1, mb: 2 }}
        />

        <Accordion sx={{ boxShadow: 'none' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Gereksinimler ve Faydalar</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="subtitle2" gutterBottom>Gereksinimler:</Typography>
            <List dense>
              {level.requirements.map((req, index) => (
                <ListItem key={index} sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    ✅
                  </ListItemIcon>
                  <ListItemText primary={req} />
                </ListItem>
              ))}
            </List>
            
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Faydalar:</Typography>
            <List dense>
              {level.benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <StarIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText primary={benefit} />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      </LevelCard>
    );
  };

  if (!calculation) {
    return (
      <Alert severity="info">
        <Typography variant="h6">ℹ️ Sertifikasyon değerlendirmesi için hesaplama gerekli</Typography>
        <Typography variant="body2">
          Sertifikasyon durumunuzu görmek için önce bir karbon ayak izi hesaplaması yapın.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Genel Durum Özeti */}
      <Accordion expanded={expandedSections.overview} onChange={() => handleSectionToggle('overview')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrophyIcon color="primary" />
            <Typography variant="h6">Sertifikasyon Durumu Özeti</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {currentStatus && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6">Mevcut Performans</Typography>
              <Typography variant="body1">
                <strong>{Math.round(calculation.perRoomNight)} kg CO₂e/oda-gece</strong> ile {currentStatus.status}
              </Typography>
            </Alert>
          )}

          <Grid container spacing={2}>
            {currentStatus && Object.entries(currentStatus.evaluations).map(([key, evaluation]) => {
              const cert = certifications[key];
              const achievedCount = evaluation.achieved.length;
              const totalLevels = cert.levels.length;
              
              return (
                <Grid item xs={12} md={4} key={key}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" component="div">
                      {cert.logo}
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {cert.name}
                    </Typography>
                    <Typography variant="h3" color="primary">
                      {achievedCount}/{totalLevels}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Seviye Tamamlandı
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(achievedCount / totalLevels) * 100}
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Green Key Sertifikasyonu */}
      <Accordion expanded={expandedSections.greenKey} onChange={() => handleSectionToggle('greenKey')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <span style={{ fontSize: '1.5rem' }}>🗝️</span>
            <Typography variant="h6">Green Key Sertifikasyonu</Typography>
            <Chip label="Popüler" color="success" size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            {certifications.greenKey.description}
          </Typography>
          
          <Button
            variant="outlined"
            href={certifications.greenKey.website}
            target="_blank"
            sx={{ mb: 3 }}
          >
            🔗 Resmi Website
          </Button>

          <Grid container spacing={2}>
            {certifications.greenKey.levels.map(level => 
              renderCertificationLevel(
                certifications.greenKey, 
                level, 
                currentStatus, 
                calculation.perRoomNight
              )
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* LEED Sertifikasyonu */}
      <Accordion expanded={expandedSections.leed} onChange={() => handleSectionToggle('leed')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <span style={{ fontSize: '1.5rem' }}>🏢</span>
            <Typography variant="h6">LEED Sertifikasyonu</Typography>
            <Chip label="Prestijli" color="primary" size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            {certifications.leed.description}
          </Typography>
          
          <Button
            variant="outlined"
            href={certifications.leed.website}
            target="_blank"
            sx={{ mb: 3 }}
          >
            🔗 Resmi Website
          </Button>

          <Grid container spacing={2}>
            {certifications.leed.levels.map(level => 
              renderCertificationLevel(
                certifications.leed, 
                level, 
                currentStatus, 
                calculation.perRoomNight
              )
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ISO 14001 */}
      <Accordion expanded={expandedSections.iso14001} onChange={() => handleSectionToggle('iso14001')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <span style={{ fontSize: '1.5rem' }}>📋</span>
            <Typography variant="h6">ISO 14001</Typography>
            <Chip label="Standart" color="warning" size="small" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            {certifications.iso14001.description}
          </Typography>
          
          <Button
            variant="outlined"
            href={certifications.iso14001.website}
            target="_blank"
            sx={{ mb: 3 }}
          >
            🔗 Resmi Website
          </Button>

          <Grid container spacing={2}>
            {certifications.iso14001.levels.map(level => 
              renderCertificationLevel(
                certifications.iso14001, 
                level, 
                currentStatus, 
                calculation.perRoomNight
              )
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Diğer Sertifikasyonlar */}
      <Accordion expanded={expandedSections.other} onChange={() => handleSectionToggle('other')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <GlobalIcon color="info" />
            <Typography variant="h6">Diğer Sertifikasyonlar</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {otherCertifications.map((cert, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Typography variant="h4">{cert.logo}</Typography>
                      <Typography variant="h6">{cert.name}</Typography>
                    </Box>
                    <Typography variant="body2" paragraph>
                      {cert.description}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                      Odak: {cert.focus}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      href={cert.website}
                      target="_blank"
                      fullWidth
                    >
                      🔗 Detaylar
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Sertifikasyon Yol Haritası */}
      <Accordion expanded={expandedSections.roadmap} onChange={() => handleSectionToggle('roadmap')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <ProgressIcon color="success" />
            <Typography variant="h6">Sertifikasyon Yol Haritası</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6">Önerilen Adımlar</Typography>
            <Typography variant="body2">
              Mevcut performansınızdan yola çıkarak öncelikli sertifikasyon hedefleriniz.
            </Typography>
          </Alert>

          <Stepper orientation="vertical">
            <Step>
              <StepLabel>
                <Typography variant="h6">1. Kısa Vadeli (3-6 ay)</Typography>
              </StepLabel>
              <StepContent>
                <Typography paragraph>
                  <strong>ISO 14001 Basic Implementation</strong> hedefine odaklanın.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Çevre politikası oluşturun" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Enerji ve su tüketim takibini başlatın" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Personel çevre eğitimleri düzenleyin" />
                  </ListItem>
                </List>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>
                <Typography variant="h6">2. Orta Vadeli (6-12 ay)</Typography>
              </StepLabel>
              <StepContent>
                <Typography paragraph>
                  <strong>Green Key Bronze/Silver</strong> seviyelerini hedefleyin.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Enerji verimli ekipmanları değiştirin" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Su tasarrufu sistemleri kurun" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Atık ayrıştırma programını iyileştirin" />
                  </ListItem>
                </List>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>
                <Typography variant="h6">3. Uzun Vadeli (1-2 yıl)</Typography>
              </StepLabel>
              <StepContent>
                <Typography paragraph>
                  <strong>LEED Certified/Silver</strong> veya <strong>Green Key Gold</strong> hedefleyin.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Yenilenebilir enerji sistemleri kurun" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Sürdürülebilir tedarik zinciri oluşturun" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Misafir katılım programları başlatın" />
                  </ListItem>
                </List>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>
                <Typography variant="h6">4. Vizes Hedefler (2+ yıl)</Typography>
              </StepLabel>
              <StepContent>
                <Typography paragraph>
                  <strong>LEED Gold/Platinum</strong> veya <strong>Green Key Platinum</strong> seviyesi.
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Net sıfır karbon hedefi belirleyin" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Sıfır atık programını tamamlayın" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                    <ListItemText primary="Biyoçeşitlilik koruma projeleri başlatın" />
                  </ListItem>
                </List>
              </StepContent>
            </Step>
          </Stepper>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TourismCertifications;
